/** Actions ▾ d’une ligne : éditer, reçu, mail, remboursement. */
import { useState } from "react";
import { facturxFetch } from "../../api/client";
import { PaymentEditModal, type PaymentPatch } from "./PaymentEditModal";
import { PaymentMenu } from "./PaymentMenu";
import { PaymentRefundModal } from "./PaymentRefundModal";
import { printPaymentReceipt } from "./paymentPdf";
import { asPayment, type Payment } from "./types";

type PaymentLineActionsProps = {
  row: Payment;
  decimalComma: boolean;
  busy?: boolean;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onBusy: (busy: boolean) => void;
  onError: (msg: string) => void;
  onNotice?: (msg: string) => void;
  onChanged: (row: Payment) => void;
};

export function PaymentLineActions({
  row,
  decimalComma,
  busy,
  open,
  onOpenChange,
  onBusy,
  onError,
  onNotice,
  onChanged,
}: PaymentLineActionsProps) {
  const [edit, setEdit] = useState(false);
  const [refund, setRefund] = useState(false);

  async function save(patch: PaymentPatch) {
    onBusy(true);
    onError("");
    try {
      const data = await facturxFetch<{ payment: Payment }>(`payments.php?id=${row.id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      onChanged(asPayment(data.payment));
      setEdit(false);
    } catch (e) {
      onError(e instanceof Error ? e.message : "Erreur");
    } finally {
      onBusy(false);
    }
  }

  async function sendReceipt() {
    onBusy(true);
    onError("");
    try {
      const data = await facturxFetch<{ sent: boolean; to: string; message?: string }>(`payments.php?id=${row.id}&action=send`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      onNotice?.(data.sent ? `Reçu envoyé à ${data.to}.` : data.message || "Le reçu n’est pas parti.");
    } catch (e) {
      onError(e instanceof Error ? e.message : "Erreur");
    } finally {
      onBusy(false);
    }
  }

  async function doRefund(amountCents: number) {
    onBusy(true);
    onError("");
    try {
      const data = await facturxFetch<{ payment: Payment }>(`payments.php?id=${row.id}&action=refund`, {
        method: "POST",
        body: JSON.stringify({ amount_cents: amountCents }),
      });
      onChanged(asPayment(data.payment));
      setRefund(false);
    } catch (e) {
      onError(e instanceof Error ? e.message : "Erreur");
    } finally {
      onBusy(false);
    }
  }

  return (
    <>
      <PaymentMenu
        busy={busy}
        canRefund={row.applied_cents > 0}
        open={open}
        onOpenChange={onOpenChange}
        onEdit={() => setEdit(true)}
        onPrint={() => printPaymentReceipt(row.id, row.number || undefined)}
        onSend={() => void sendReceipt()}
        onRefund={() => setRefund(true)}
      />
      <PaymentEditModal
        open={edit}
        row={row}
        decimalComma={decimalComma}
        busy={busy}
        onCancel={() => setEdit(false)}
        onConfirm={(patch) => void save(patch)}
      />
      <PaymentRefundModal
        open={refund}
        remainingCents={row.applied_cents}
        decimalComma={decimalComma}
        busy={busy}
        onCancel={() => setRefund(false)}
        onConfirm={(cents) => void doRefund(cents)}
      />
    </>
  );
}
